import { Component, Inject, OnDestroy, OnInit, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ActivatedRoute } from '@angular/router';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Layout, TreoConfig } from 'app/layout/layout.types';
import { Account, Activity } from '@core/models';
import { CustomDrawerContract, LayoutService } from './layout.service';
import { TreoConfigService, TreoDrawerComponent } from 'tsui/@treo';
import { ReleaseNotesService } from '@core/services/release-notes.service';
import { AccountState } from '@state/account/state';
import { Store } from '@ngxs/store';

@Component({
  selector: 'layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LayoutComponent extends OnDestroyMixin implements OnInit, OnDestroy {
  config: TreoConfig;
  layout: Layout;
  account: Account;
  activities: Activity[];

  @ViewChild('customDrawer', { static: true }) customDrawer: TreoDrawerComponent;
  @ViewChild('childLoader', { static: true, read: ViewContainerRef }) dynamicChild: ViewContainerRef;

  constructor(
    @Inject(DOCUMENT) private _document: any,
    private _activatedRoute: ActivatedRoute,
    private _treoConfigService: TreoConfigService,
    private store: Store,
    private readonly layoutService: LayoutService,
    private readonly releasenotesService: ReleaseNotesService,
  ) {
    super();
    this.account = this.store.selectSnapshot(AccountState.getAccount);
    this.releasenotesService.loadReleaseNotesScript();
  }

  ngOnInit(): void {
    // Subscribe to config changes
    this._treoConfigService.config$.pipe(untilComponentDestroyed(this)).subscribe((config: TreoConfig) => {
      // Store the config
      this.config = config;

      // Update the selected theme class name on body
      const themeName = 'treo-theme-' + config.theme;
      this._document.body.classList.forEach((className) => {
        if (className.startsWith('treo-theme-') && className !== themeName) {
          this._document.body.classList.remove(className);
          this._document.body.classList.add(themeName);
          return;
        }
      });

      // Update the layout
      this._updateLayout();
    });

    this.layoutService.toggle$
      .pipe(untilComponentDestroyed(this))
      .subscribe(({ componentClass, options: { inputs, outputs } }: CustomDrawerContract) => {
        const component = this.dynamicChild.createComponent(componentClass);

        for (const inputKey in inputs) {
          component.instance[inputKey] = inputs[inputKey];
        }

        for (const outputKey in outputs) {
          component.instance[outputKey].pipe(untilComponentDestroyed(this)).subscribe(outputs[outputKey]);
        }

        this.customDrawer.toggle();
      });

    this.layoutService.close$.pipe(untilComponentDestroyed(this)).subscribe(() => this.customDrawer.close());
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  private _updateLayout(): void {
    // Get the current activated route
    let route = this._activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }

    // 1. Set the layout from the config
    this.layout = this.config.layout;

    // 2. Get the query parameter from the current route and
    // set the layout and save the layout to the config
    const layoutFromQueryParam = route.snapshot.queryParamMap.get('layout') as Layout;
    if (layoutFromQueryParam) {
      this.config.layout = this.layout = layoutFromQueryParam;
    }

    // 3. Iterate through the paths and change the layout as we find
    // a config for it.
    //
    // The reason we do this is that there might be empty grouping
    // paths or componentless routes along the path. Because of that,
    // we cannot just assume that the layout configuration will be
    // in the last path's config or in the first path's config.
    //
    // So, we get all the paths that matched starting from root all
    // the way to the current activated route, walk through them one
    // by one and change the layout as we find the layout config. This
    // way, layout configuration can live anywhere within the path and
    // we won't miss it.
    //
    // Also, this will allow overriding the layout in any time so we
    // can have different layouts for different routes.
    const paths = route.pathFromRoot;
    paths.forEach((path) => {
      // Check if there is a 'layout' data
      if (path.routeConfig && path.routeConfig.data && path.routeConfig.data.layout) {
        // Set the layout
        this.layout = path.routeConfig.data.layout;
      }
    });
  }

  customDrawerOpenedChanged(event: boolean): void {
    if (event === false) {
      this.dynamicChild.clear();
      this.layoutService.closeComponentDrawer();
    }
  }
}
